import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ClockIcon,
  MicrophoneIcon,
  PlayIcon,
  PlusIcon,
  StopIcon,
} from '@heroicons/react/24/outline';
import { useI18n } from '../i18n/I18nProvider';
import { IeltsSpeakingResultAdapter } from '../components/PracticeResultAdapters';
import IeltsAuthenticSimulation from '../components/IeltsAuthenticSimulation';
import {
  buildIeltsSpeakingLaunchUrl,
  createIeltsRequestId,
  createIeltsTimerState,
  createIeltsSpeakingThread,
  fetchIeltsSpeakingAttempt,
  fetchIeltsSpeakingHistory,
  fetchIeltsSpeakingThread,
  fetchIeltsSpeakingThreads,
  formatIeltsBand,
  formatIeltsTimer,
  getIeltsAssessmentScope,
  getIeltsResultActions,
  getIeltsUploadProgress,
  IELTS_MAX_DURATION_SECONDS,
  IELTS_MIN_DURATION_SECONDS,
  isAuthenticIeltsConfig,
  normalizeIeltsAssessment,
  normalizeIeltsConfig,
  normalizeIeltsHistory,
  serializeIeltsSpeakingConfig,
  retryIeltsSpeakingScoring,
  tickIeltsTimer,
  transitionIeltsRecordingState,
  uploadIeltsSpeakingRecording,
  startAssignedIeltsSpeakingAttempt,
  startIeltsSpeakingAttempt,
} from '../lib/ieltsSpeaking';
import {
  fetchPracticeAssignment,
  practiceAssignmentFromPayload,
} from '../lib/practiceAttempts';

const EMPTY_CONFIG = {
  question: '',
  part: '',
  suggested_duration_seconds: 90,
  strict_timer: false,
};

const assignedQuery = (searchParams) => ({
  assignmentId: searchParams.get('assignment_id') || '',
  targetId: searchParams.get('target_id') || '',
  attemptId: searchParams.get('attempt_id') || '',
  threadId: searchParams.get('thread_id') || '',
  historyOnly: searchParams.get('history') === '1',
});

const preferredRecordingType = () => {
  if (typeof window === 'undefined' || !window.MediaRecorder) return '';
  return [
    'audio/webm;codecs=opus',
    'audio/webm',
    'audio/ogg;codecs=opus',
    'audio/mp4',
  ].find((type) => window.MediaRecorder.isTypeSupported?.(type)) || '';
};

const getResponseResult = (payload) => ({
  ...payload,
  ...payload?.attempt,
  ...payload?.result,
  ...payload?.assessment,
  ...normalizeIeltsAssessment(payload),
});

const errorMessage = (error, t) => {
  if (error?.status === 401 || error?.status === 403) return t('You do not have access to this practice.');
  if (error?.status === 404) return t('This IELTS speaking practice could not be found.');
  return error?.message ? t(error.message) : t('The IELTS speaking request failed.');
};

const stateLabel = (state, t) => ({
  idle: t('Ready to record'),
  requesting_microphone: t('Requesting microphone access...'),
  recording: t('Recording'),
  soft_timeout: t('Suggested time reached; you can continue.'),
  strict_timeout: t('Strict timer reached; saving your recording.'),
  uploading: t('Uploading recording...'),
  fresh_grading: t('Fresh grading in progress...'),
  coaching: t('Preparing continuous coaching...'),
  result: t('Result ready'),
  retryable_failure: t('This recording can be retried.'),
}[state] || t('Ready to record'));

const ConfigForm = ({ config, onChange, disabled, t }) => {
  const update = (patch) => onChange({ ...config, ...patch });
  return (
    <section className="border border-gray-200 bg-white p-5 sm:p-6" aria-label={t('IELTS speaking settings')}>
      <div>
        <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Independent practice')}</div>
        <h1 className="mt-1 text-2xl font-bold text-gray-950">{t('IELTS Speaking Practice')}</h1>
        <p className="mt-2 text-sm leading-6 text-gray-600">{t('Record one answer, get a predicted band, and keep coaching history.')}</p>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className="block text-sm font-medium text-gray-800 sm:col-span-2">
          {t('Speaking question')} <span className="font-normal text-gray-500">({t('optional')})</span>
          <textarea
            value={config.question}
            onChange={(event) => update({ question: event.target.value })}
            disabled={disabled}
            className="mt-1 min-h-24 w-full resize-y rounded border border-gray-300 px-3 py-2"
            placeholder={t('Leave empty for a general speaking assessment.')}
          />
        </label>
        <label className="block text-sm font-medium text-gray-800">
          {t('Part')} <span className="font-normal text-gray-500">({t('optional')})</span>
          <select value={config.part} onChange={(event) => update({ part: event.target.value })} disabled={disabled} className="mt-1 w-full rounded border border-gray-300 bg-white p-2.5">
            <option value="">{t('Not specified')}</option>
            <option value="1">{t('Part 1')}</option>
            <option value="2">{t('Part 2')}</option>
            <option value="3">{t('Part 3')}</option>
          </select>
        </label>
        <label className="block text-sm font-medium text-gray-800">
          {t('Suggested answer time')}
          <input
            type="number"
            min={IELTS_MIN_DURATION_SECONDS}
            max={IELTS_MAX_DURATION_SECONDS}
            step="5"
            value={config.suggested_duration_seconds}
            onChange={(event) => update({ suggested_duration_seconds: event.target.value })}
            disabled={disabled}
            className="mt-1 w-full rounded border border-gray-300 px-3 py-2"
          />
          <span className="mt-1 block text-xs font-normal text-gray-500">{t('15 to 300 seconds')}</span>
        </label>
        <label className="inline-flex items-start gap-2 text-sm text-gray-800 sm:col-span-2">
          <input type="checkbox" checked={config.strict_timer} onChange={(event) => update({ strict_timer: event.target.checked })} disabled={disabled} className="mt-0.5 h-4 w-4 text-indigo-600" />
          <span><span className="font-semibold">{t('Strict timer')}</span><span className="mt-0.5 block text-xs text-gray-600">{t('Stop the microphone automatically at the suggested time.')}</span></span>
        </label>
      </div>
      <div className="mt-4 border-l-2 border-indigo-400 pl-3 text-sm leading-6 text-gray-700">
        <span className="font-semibold">{t('Assessment scope')}:</span>{' '}
        {getIeltsAssessmentScope(config) === 'general'
          ? t('General speaking assessment')
          : t('Prompt-based assessment')}
        <span className="mt-1 block text-xs text-gray-500">{t('Both question and Part can be empty. No question will be generated.')}</span>
      </div>
    </section>
  );
};

export const IeltsSpeakingRecordingStatus = ({ state, timer, assessmentProgress, t }) => (
  <div className="border border-gray-200 bg-white p-4" aria-live="polite">
    <div className="flex flex-wrap items-center justify-between gap-3">
      <span className="inline-flex items-center gap-2 text-sm font-semibold text-gray-800">
        {state === 'recording' || state === 'soft_timeout' || state === 'strict_timeout'
          ? <MicrophoneIcon className="h-5 w-5 text-rose-600" aria-hidden="true" />
          : <ClockIcon className="h-5 w-5 text-indigo-600" aria-hidden="true" />}
        {stateLabel(state, t)}
      </span>
      {['recording', 'soft_timeout', 'strict_timeout'].includes(state) && (
        <span className={`font-mono text-lg font-bold tabular-nums ${state === 'strict_timeout' ? 'text-rose-700' : 'text-indigo-700'}`} data-testid="ielts-timer">
          {formatIeltsTimer(timer.remaining_ms)}
        </span>
      )}
    </div>
    {['recording', 'soft_timeout', 'strict_timeout'].includes(state) && (
      <div className="mt-3 h-2 overflow-hidden bg-gray-100" aria-hidden="true">
        <div className={`h-full ${state === 'strict_timeout' ? 'bg-rose-500' : state === 'soft_timeout' ? 'bg-amber-500' : 'bg-indigo-600'}`} style={{ width: `${Math.min(100, Math.max(0, (timer.elapsed_ms / timer.limit_ms) * 100))}%` }} />
      </div>
    )}
    {assessmentProgress != null && ['uploading', 'fresh_grading', 'coaching', 'result', 'retryable_failure'].includes(state) && (
      <div className="mt-3" aria-live="polite">
        <div className="mb-1 flex items-center justify-between gap-3 text-xs font-medium text-gray-600">
          <span>
            {assessmentProgress >= 100
              ? t('Assessment complete')
              : assessmentProgress >= 67
                ? t('Recording uploaded. AI scoring is in progress.')
                : t('Uploading recording...')}
          </span>
          <span className="tabular-nums" data-i18n-skip>{Math.round(assessmentProgress)}%</span>
        </div>
        <div
          className="h-2 overflow-hidden bg-gray-100"
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow={Math.round(assessmentProgress)}
        >
          <div
            className={`h-full transition-[width] duration-300 ${assessmentProgress >= 100 ? 'bg-emerald-600' : 'bg-indigo-600'}`}
            style={{ width: `${Math.min(100, Math.max(0, assessmentProgress))}%` }}
          />
        </div>
      </div>
    )}
  </div>
);

const History = ({ entries, currentId, onSelect, t, locale }) => {
  if (!entries.length) return null;
  const newest = entries[0]?.predicted_overall;
  const previous = entries[1]?.predicted_overall;
  const delta = newest != null && previous != null ? Math.round((newest - previous) * 2) / 2 : null;
  return (
    <section className="mt-7 border-t border-gray-200 pt-6" aria-label={t('IELTS speaking history')}>
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t('IELTS speaking history')}</h2>
          <p className="mt-1 text-sm text-gray-500">{t('Every attempt stays available with its score and answer details.')}</p>
        </div>
        {delta != null && <span className={`text-sm font-semibold ${delta >= 0 ? 'text-emerald-700' : 'text-rose-700'}`}>{delta >= 0 ? '+' : '-'}{Math.abs(delta).toFixed(1).replace(/\.0$/, '')} {t('band change')}</span>}
      </div>
      <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
        {entries.map((entry, index) => {
          const id = entry.id || entry.attempt_id || `${entry.created_at || 'entry'}-${index}`;
          const date = entry.created_at ? new Date(entry.created_at).toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
          return (
            <button type="button" key={id} onClick={() => onSelect(entry)} className={`flex w-full flex-col gap-2 px-1 py-4 text-left sm:flex-row sm:items-center sm:justify-between ${currentId === id ? 'bg-indigo-50' : 'hover:bg-gray-50'}`}>
              <span className="min-w-0">
                <span className="flex flex-wrap items-center gap-2 font-semibold text-gray-900">
                  {t('Attempt {number}', { number: entry.attempt_no || Math.max(1, entries.length - index) })}
                  {index === 0 && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700"><CheckCircleIcon className="h-4 w-4" aria-hidden="true" />{t('Latest')}</span>}
                </span>
                <span className="mt-1 block text-sm text-gray-500" data-i18n-skip>{date || t('Not available')}</span>
              </span>
              <span className="text-lg font-bold tabular-nums text-indigo-800" data-i18n-skip>{entry.predicted_overall == null ? t('Not available') : formatIeltsBand(entry.predicted_overall)}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
};

const SessionHistory = ({ sessions, currentThreadId, onResume, onSelectAttempt, t, locale }) => {
  if (!sessions.length) {
    return <p className="mt-4 border border-gray-200 bg-gray-50 p-5 text-sm text-gray-500">{t('No IELTS speaking sessions yet.')}</p>;
  }
  return (
    <section className="mt-7 border-t border-gray-200 pt-6" aria-label={t('IELTS speaking sessions')}>
      <h2 className="text-lg font-bold text-gray-900">{t('IELTS speaking sessions')}</h2>
      <p className="mt-1 text-sm text-gray-500">{t('Each session keeps its own attempts and coaching context.')}</p>
      <div className="mt-4 divide-y divide-gray-200 border-y border-gray-200">
        {sessions.map((session, index) => {
          const attempts = Array.isArray(session.attempts) ? session.attempts : [];
          const sessionDate = session.created_at
            ? new Date(session.created_at).toLocaleString(locale === 'zh-CN' ? 'zh-CN' : 'en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
            : '';
          return (
            <div key={session.id} className={`py-4 ${currentThreadId === session.id ? 'bg-indigo-50' : ''}`}>
              <div className="flex flex-wrap items-center justify-between gap-3 px-2">
                <div>
                  <div className="font-semibold text-gray-900">{t('Session {number}', { number: sessions.length - index })}</div>
                  <div className="mt-1 text-sm text-gray-500" data-i18n-skip>{sessionDate}</div>
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  <span className="text-sm text-gray-600">{t('{count} attempts', { count: attempts.length || session.attempt_count || 0 })}</span>
                  <button type="button" onClick={() => onResume(session)} className="rounded border border-indigo-300 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 hover:bg-indigo-50">
                    {t('Continue this session')}
                  </button>
                </div>
              </div>
              {attempts.length > 0 && (
                <div className="mt-3 grid gap-2 px-2 sm:grid-cols-2">
                  {attempts.map((attempt) => (
                    <button type="button" key={attempt.id} onClick={() => onSelectAttempt(session, attempt)} className="flex items-center justify-between gap-3 border border-gray-200 bg-white px-3 py-2 text-left hover:border-indigo-300 hover:bg-indigo-50">
                      <span className="text-sm font-medium text-gray-800">{t('Attempt {number}', { number: attempt.attempt_no || 1 })}</span>
                      <span className="font-bold text-indigo-800" data-i18n-skip>{attempt.predicted_overall == null ? t('Not available') : formatIeltsBand(attempt.predicted_overall)}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default function IeltsSpeakingPage() {
  const { locale, t } = useI18n();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const query = useMemo(() => assignedQuery(searchParams), [searchParams]);
  const assigned = Boolean(query.assignmentId);
  const [phase, setPhase] = useState(assigned ? 'record' : 'setup');
  const [config, setConfig] = useState(EMPTY_CONFIG);
  const [assignment, setAssignment] = useState(null);
  const [threadId, setThreadId] = useState(query.threadId);
  const [attemptId, setAttemptId] = useState(query.attemptId);
  const [targetId, setTargetId] = useState(query.targetId);
  const [recordingState, setRecordingState] = useState('idle');
  const [assessmentProgress, setAssessmentProgress] = useState(null);
  const [timer, setTimer] = useState(() => createIeltsTimerState());
  const [localRecording, setLocalRecording] = useState(null);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [contextLoading, setContextLoading] = useState(assigned);
  const [error, setError] = useState('');
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const startedAtRef = useRef(0);
  const stoppedByRef = useRef('manual');
  const timerRef = useRef(timer);
  const recordingStateRef = useRef(recordingState);
  const localRecordingRef = useRef(null);
  const attemptIdRef = useRef(attemptId);
  const threadIdRef = useRef(threadId);
  const targetIdRef = useRef(targetId);
  const configRef = useRef(config);
  const recordingGenerationRef = useRef(0);
  const assignedLoadRef = useRef('');
  const autonomousHistoryLoadRef = useRef('');
  const launchRequestRef = useRef(createIeltsRequestId('page-launch'));

  useEffect(() => { recordingStateRef.current = recordingState; }, [recordingState]);
  useEffect(() => { timerRef.current = timer; }, [timer]);
  useEffect(() => { localRecordingRef.current = localRecording; }, [localRecording]);
  useEffect(() => { attemptIdRef.current = attemptId; }, [attemptId]);
  useEffect(() => { threadIdRef.current = threadId; }, [threadId]);
  useEffect(() => { targetIdRef.current = targetId; }, [targetId]);
  useEffect(() => { configRef.current = config; }, [config]);

  const releaseStream = useCallback(() => {
    streamRef.current?.getTracks?.().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const replaceLocalRecording = useCallback((next) => {
    if (localRecordingRef.current?.url) URL.revokeObjectURL(localRecordingRef.current.url);
    const value = next?.blob
      ? { ...next, url: typeof URL !== 'undefined' && URL.createObjectURL ? URL.createObjectURL(next.blob) : '' }
      : null;
    localRecordingRef.current = value;
    setLocalRecording(value);
  }, []);

  const loadHistory = useCallback(async (currentThreadId = threadId) => {
    try {
      if (currentThreadId) {
        setHistory(normalizeIeltsHistory(await fetchIeltsSpeakingHistory({ threadId: currentThreadId, limit: 50 })));
        return;
      }
      setHistory([]);
    } catch (_) {
      setHistory([]);
    }
  }, [threadId]);

  const loadSessions = useCallback(async () => {
    if (!query.assignmentId) return [];
    try {
      const items = await fetchIeltsSpeakingThreads({ assignmentId: query.assignmentId, limit: 100, includeAttempts: true });
      setSessions(items);
      return items;
    } catch (_) {
      setSessions([]);
      return [];
    }
  }, [query.assignmentId]);

  const startAssignedAttempt = useCallback(async (requestId = createIeltsRequestId('assigned-session')) => {
    const payload = await startAssignedIeltsSpeakingAttempt(query.assignmentId, requestId);
    const nextThreadId = payload.thread?.id || payload.thread_id || '';
    const nextAttemptId = payload.ielts?.id || payload.ielts_attempt_id || '';
    const nextTargetId = payload.target?.id || payload.target_id || query.targetId;
    if (!nextThreadId || !nextAttemptId) throw new Error('The practice could not be opened.');
    attemptIdRef.current = nextAttemptId;
    targetIdRef.current = nextTargetId;
    threadIdRef.current = nextThreadId;
    setAttemptId(nextAttemptId);
    setTargetId(nextTargetId);
    setThreadId(nextThreadId);
    return { threadId: nextThreadId, attemptId: nextAttemptId, targetId: nextTargetId, payload };
  }, [query.assignmentId, query.targetId]);

  useEffect(() => {
    let active = true;
    let settled = false;
    const loadKey = [query.assignmentId, query.threadId, query.attemptId, query.historyOnly ? 'history' : 'practice'].join(':');
    if (!assigned || assignedLoadRef.current === loadKey) return undefined;
    assignedLoadRef.current = loadKey;
    (async () => {
      setContextLoading(true);
      try {
        const response = await fetchPracticeAssignment(query.assignmentId);
        if (!active) return;
        const nextAssignment = practiceAssignmentFromPayload(response);
        if (!nextAssignment || nextAssignment.practice_type !== 'ielts_speaking') {
          throw new Error('This IELTS speaking practice could not be found.');
        }
        setAssignment(nextAssignment);
        const nextConfig = normalizeIeltsConfig(nextAssignment.config || {});
        setConfig(nextConfig);
        if (query.historyOnly) {
          await loadSessions();
          if (!active) return;
          setPhase('history');
          setResult(null);
          setHistory([]);
          return;
        }
        setPhase('record');
        let historyThreadId = '';
        if (query.attemptId) {
          let detail;
          try {
            detail = await fetchIeltsSpeakingAttempt(query.attemptId);
          } catch (attemptError) {
            if (attemptError?.status !== 404) throw attemptError;
            const started = await startAssignedAttempt(launchRequestRef.current);
            historyThreadId = started.threadId;
          }
          if (!active) return;
          if (detail) {
            historyThreadId = detail.thread_id || query.threadId;
            setThreadId(historyThreadId);
            setAttemptId(detail.attempt_id || detail.id || query.attemptId);
            setConfig(normalizeIeltsConfig(detail));
            if (detail.status === 'started' && !detail.recording_id) {
              setResult(null);
              setRecordingState('idle');
            } else {
              setResult(detail);
              setRecordingState('result');
            }
          }
        } else if (query.threadId) {
          const thread = await fetchIeltsSpeakingThread(query.threadId);
          const started = await startIeltsSpeakingAttempt(query.threadId, launchRequestRef.current);
          if (!active) return;
          historyThreadId = query.threadId;
          setThreadId(query.threadId);
          setAttemptId(started.attempt?.id || started.attempt_id || '');
          setConfig(normalizeIeltsConfig(thread.prompt || thread.config || nextConfig));
        } else {
          const started = await startAssignedAttempt(launchRequestRef.current);
          historyThreadId = started.threadId;
        }
        if (!active) return;
        setContextLoading(false);
        void Promise.allSettled([
          loadSessions(),
          loadHistory(historyThreadId),
        ]);
      } catch (loadError) {
        if (active) {
          assignedLoadRef.current = '';
          setError(errorMessage(loadError, t));
        }
      } finally {
        settled = true;
        if (active) setContextLoading(false);
      }
    })();
    return () => {
      active = false;
      // React StrictMode intentionally mounts effects twice in development.
      // Release an in-flight key so the second mount can own the request.
      if (!settled && assignedLoadRef.current === loadKey) assignedLoadRef.current = '';
    };
  }, [assigned, loadHistory, loadSessions, query.assignmentId, query.attemptId, query.historyOnly, query.targetId, query.threadId, startAssignedAttempt, t]);

  useEffect(() => {
    let active = true;
    let settled = false;
    if (assigned || !query.attemptId || autonomousHistoryLoadRef.current === query.attemptId) return undefined;
    autonomousHistoryLoadRef.current = query.attemptId;
    (async () => {
      setContextLoading(true);
      setError('');
      try {
        const detail = await fetchIeltsSpeakingAttempt(query.attemptId);
        if (!active) return;
        const nextThreadId = detail.thread_id || query.threadId;
        setConfig(normalizeIeltsConfig(detail));
        setThreadId(nextThreadId);
        setAttemptId(detail.attempt_id || query.attemptId);
        setResult(detail);
        setRecordingState('result');
        setPhase('record');
        await loadHistory(nextThreadId);
      } catch (loadError) {
        if (active) {
          autonomousHistoryLoadRef.current = '';
          setError(errorMessage(loadError, t));
        }
      } finally {
        settled = true;
        if (active) setContextLoading(false);
      }
    })();
    return () => {
      active = false;
      if (!settled && autonomousHistoryLoadRef.current === query.attemptId) {
        autonomousHistoryLoadRef.current = '';
      }
    };
  }, [assigned, loadHistory, query.attemptId, query.threadId, t]);

  useEffect(() => () => {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop();
    releaseStream();
    if (localRecordingRef.current?.url) URL.revokeObjectURL(localRecordingRef.current.url);
  }, [releaseStream]);

  const submitBlob = useCallback(async (
    blob,
    durationMs,
    stoppedBy = 'manual',
    reuseStoredRecording = false,
    recordingContext = null,
  ) => {
    const activeAttemptId = recordingContext?.attemptId || attemptIdRef.current;
    const activeThreadId = recordingContext?.threadId || threadIdRef.current;
    const activeTargetId = recordingContext?.targetId || targetIdRef.current;
    const activeConfig = recordingContext?.config || configRef.current;
    if (!blob || (!activeThreadId && !query.assignmentId)) return;
    recordingStateRef.current = 'uploading';
    setRecordingState('uploading');
    setAssessmentProgress(reuseStoredRecording && activeAttemptId ? 67 : 0);
    setError('');
    try {
      let payload;
      if (reuseStoredRecording && activeAttemptId) {
        try {
          payload = await retryIeltsSpeakingScoring({ attemptId: activeAttemptId, threadId: activeThreadId });
        } catch (retryError) {
          if (retryError?.code !== 'ielts_speaking.recording_required') throw retryError;
          setAssessmentProgress(0);
        }
      }
      if (!payload) {
        payload = await uploadIeltsSpeakingRecording({
          blob,
          config: activeConfig,
          threadId: activeThreadId,
          assignmentId: query.assignmentId,
          targetId: activeTargetId,
          attemptId: activeAttemptId,
          durationMs,
          stoppedBy,
          onUploadProgress: (progress) => {
            const nextProgress = getIeltsUploadProgress(progress);
            setAssessmentProgress((current) => Math.max(current || 0, nextProgress));
          },
        });
      }
      setAssessmentProgress((current) => Math.max(current || 0, 67));
      void loadHistory(payload.thread_id || activeThreadId);
      const gradingState = transitionIeltsRecordingState('uploading', 'GRADING_STARTED');
      recordingStateRef.current = gradingState;
      setRecordingState(gradingState);
      let completedPayload = payload;
      const payloadAttemptId = payload.attempt_id || payload.attempt?.id || activeAttemptId;
      if (payloadAttemptId && normalizeIeltsAssessment(payload).predicted_overall == null) {
        completedPayload = await fetchIeltsSpeakingAttempt(payloadAttemptId);
      }
      const coachingState = transitionIeltsRecordingState('fresh_grading', 'COACHING_STARTED');
      recordingStateRef.current = coachingState;
      setRecordingState(coachingState);
      const nextResult = getResponseResult(completedPayload);
      const scoredDimensions = Object.values(nextResult.dimensions || {})
        .filter((value) => value != null);
      if (nextResult.predicted_overall == null || scoredDimensions.length !== 4) {
        throw new Error('The IELTS speaking assessment was incomplete. Retry scoring.');
      }
      const completedAttemptId = payloadAttemptId || nextResult.attempt_id || '';
      const completedThreadId = nextResult.thread_id || activeThreadId;
      attemptIdRef.current = completedAttemptId;
      threadIdRef.current = completedThreadId;
      setAttemptId(completedAttemptId);
      setThreadId(completedThreadId);
      const resultState = transitionIeltsRecordingState('coaching', 'RESULT_READY');
      recordingStateRef.current = resultState;
      setRecordingState(resultState);
      setAssessmentProgress(100);
      setResult({
        ...nextResult,
        ...serializeIeltsSpeakingConfig(activeConfig),
        thread_id: completedThreadId,
        attempt_id: completedAttemptId,
        recording_url: nextResult.recording_url || localRecordingRef.current?.url || '',
      });
      await loadHistory(completedThreadId);
      await loadSessions();
    } catch (submitError) {
      const failedAttempt = submitError?.payload?.attempt || {};
      if (failedAttempt.id) setAttemptId(failedAttempt.id);
      if (failedAttempt.thread_id) setThreadId(failedAttempt.thread_id);
      setError(errorMessage(submitError, t));
      const failureState = transitionIeltsRecordingState(recordingStateRef.current, 'RETRYABLE_FAILURE');
      recordingStateRef.current = failureState;
      setRecordingState(failureState);
    }
  }, [loadHistory, loadSessions, query.assignmentId, t]);

  useEffect(() => {
    if (!['recording', 'soft_timeout'].includes(recordingState)) return undefined;
    const timerId = window.setInterval(() => {
      const next = tickIeltsTimer(timerRef.current, Date.now() - startedAtRef.current);
      timerRef.current = next;
      setTimer(next);
      if (next.status === 'soft_timeout' && recordingStateRef.current === 'recording') {
        setRecordingState('soft_timeout');
      }
      if (next.status === 'strict_timeout' && recorderRef.current?.state === 'recording') {
        stoppedByRef.current = 'strict_timer';
        setRecordingState('strict_timeout');
        recorderRef.current.stop();
      }
    }, 100);
    return () => window.clearInterval(timerId);
  }, [recordingState]);

  const startRecording = async () => {
    const startingState = recordingStateRef.current;
    if (['requesting_microphone', 'recording', 'soft_timeout', 'uploading', 'fresh_grading', 'coaching', 'result'].includes(startingState)) return;
    if (!navigator.mediaDevices?.getUserMedia || !window.MediaRecorder) {
      setError(t('This browser does not support microphone recording.'));
      recordingStateRef.current = 'retryable_failure';
      setRecordingState('retryable_failure');
      return;
    }
    setError('');
    setAssessmentProgress(null);
    const requestingState = transitionIeltsRecordingState(startingState, 'REQUEST_MICROPHONE');
    recordingStateRef.current = requestingState;
    setRecordingState(requestingState);
    try {
      const recordingContext = {
        attemptId: attemptIdRef.current,
        threadId: threadIdRef.current,
        targetId: targetIdRef.current,
        config: configRef.current,
      };
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      stoppedByRef.current = 'manual';
      const recorder = new window.MediaRecorder(stream, preferredRecordingType() ? { mimeType: preferredRecordingType() } : undefined);
      const recordingGeneration = ++recordingGenerationRef.current;
      recorderRef.current = recorder;
      recorder.ondataavailable = (event) => {
        if (recordingGeneration === recordingGenerationRef.current && event.data?.size) {
          chunksRef.current.push(event.data);
        }
      };
      recorder.onstop = () => {
        if (recordingGeneration !== recordingGenerationRef.current) return;
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        const durationMs = Math.max(0, Date.now() - startedAtRef.current);
        releaseStream();
        replaceLocalRecording({ blob, durationMs });
        submitBlob(blob, durationMs, stoppedByRef.current, false, recordingContext);
      };
      recorder.start();
      startedAtRef.current = Date.now();
      const nextTimer = createIeltsTimerState({ mode: config.strict_timer ? 'strict' : 'soft', limitSeconds: config.suggested_duration_seconds });
      timerRef.current = nextTimer;
      setTimer(nextTimer);
      const grantedState = transitionIeltsRecordingState('requesting_microphone', 'MICROPHONE_GRANTED');
      recordingStateRef.current = grantedState;
      setRecordingState(grantedState);
    } catch (recordingError) {
      releaseStream();
      setError(recordingError?.message ? t(recordingError.message) : t('Microphone access was not granted.'));
      recordingStateRef.current = 'retryable_failure';
      setRecordingState('retryable_failure');
    }
  };

  const stopRecording = (reason = 'manual') => {
    const recorder = recorderRef.current;
    if (!recorder || recorder.state !== 'recording') return;
    stoppedByRef.current = reason;
    const currentState = recordingStateRef.current;
    setRecordingState(transitionIeltsRecordingState(currentState, 'STOP_RECORDING'));
    recorder.stop();
  };

  const startAutonomousThread = async () => {
    setContextLoading(true);
    setError('');
    try {
      const payload = await createIeltsSpeakingThread(config);
      const nextThreadId = payload.thread_id || payload.thread?.id || payload.id || '';
      if (!nextThreadId) throw new Error('The new speaking thread could not be created.');
      const nextConfig = normalizeIeltsConfig(payload.config || payload.thread?.config || config);
      const started = await startIeltsSpeakingAttempt(nextThreadId);
      const nextAttemptId = started.attempt?.id || started.attempt_id || '';
      if (!nextAttemptId) throw new Error('The practice could not be opened.');
      setThreadId(nextThreadId);
      setAttemptId(nextAttemptId);
      setConfig(nextConfig);
      setPhase('record');
      setRecordingState('idle');
      await loadHistory(nextThreadId);
    } catch (threadError) {
      setError(errorMessage(threadError, t));
    } finally {
      setContextLoading(false);
    }
  };

  const practiceAgain = async () => {
    setError('');
    if (assigned) {
      setContextLoading(true);
      try {
        if (!threadId) throw new Error('The speaking session could not be found.');
        const started = await startIeltsSpeakingAttempt(threadId);
        const nextAttemptId = started.attempt?.id || started.attempt_id || '';
        if (!nextAttemptId) throw new Error('The next speaking attempt could not be started.');
        attemptIdRef.current = nextAttemptId;
        setAttemptId(nextAttemptId);
        setResult(null);
        setAssessmentProgress(null);
        replaceLocalRecording(null);
        setTimer(createIeltsTimerState({ mode: config.strict_timer ? 'strict' : 'soft', limitSeconds: config.suggested_duration_seconds }));
        recordingStateRef.current = 'idle';
        setRecordingState('idle');
        await loadHistory(threadId);
        await loadSessions();
      } catch (startError) {
        setError(errorMessage(startError, t));
      } finally {
        setContextLoading(false);
      }
      return;
    }
    setContextLoading(true);
    try {
      const payload = await startIeltsSpeakingAttempt(threadId);
      const nextAttemptId = payload.attempt?.id || payload.attempt_id || '';
      if (!nextAttemptId) throw new Error('The next speaking attempt could not be started.');
      attemptIdRef.current = nextAttemptId;
      setAttemptId(nextAttemptId);
      setResult(null);
      setAssessmentProgress(null);
      replaceLocalRecording(null);
      setTimer(createIeltsTimerState({ mode: config.strict_timer ? 'strict' : 'soft', limitSeconds: config.suggested_duration_seconds }));
      recordingStateRef.current = 'idle';
      setRecordingState('idle');
      setPhase('record');
      await loadHistory(threadId);
    } catch (startError) {
      setError(errorMessage(startError, t));
    } finally {
      setContextLoading(false);
    }
  };

  const startNewThread = async () => {
    setError('');
    if (assigned) {
      setContextLoading(true);
      setResult(null);
      setAssessmentProgress(null);
      replaceLocalRecording(null);
      setRecordingState('idle');
      setTimer(createIeltsTimerState({ mode: config.strict_timer ? 'strict' : 'soft', limitSeconds: config.suggested_duration_seconds }));
      try {
        const started = await startAssignedAttempt(createIeltsRequestId('assigned-session'));
        setPhase('record');
        await loadHistory(started.threadId);
        await loadSessions();
      } catch (startError) {
        setError(errorMessage(startError, t));
      } finally {
        setContextLoading(false);
      }
      return;
    }
    setResult(null);
    setAssessmentProgress(null);
    setHistory([]);
    setThreadId('');
    setAttemptId('');
    setTargetId('');
    replaceLocalRecording(null);
    setRecordingState(transitionIeltsRecordingState('result', 'NEW_THREAD'));
    setConfig(EMPTY_CONFIG);
    setPhase('setup');
  };

  const retryRecording = () => {
    const previous = localRecordingRef.current;
    if (!previous?.blob) return;
    submitBlob(previous.blob, previous.durationMs, 'retry', true);
  };

  const selectHistory = async (entry) => {
    const id = entry.attempt_id || entry.id;
    if (!id) {
      setResult(entry);
      setRecordingState('result');
      return;
    }
    try {
      const detail = await fetchIeltsSpeakingAttempt(id);
      const normalized = normalizeIeltsAssessment(detail);
      setResult({ ...entry, ...normalized, ...serializeIeltsSpeakingConfig(config), recording_url: normalized.recording_url || entry.recording_url || '' });
      setRecordingState('result');
    } catch (loadError) {
      setError(errorMessage(loadError, t));
    }
  };

  const resumeSession = (session) => {
    if (!session?.id || !query.assignmentId) return;
    navigate(buildIeltsSpeakingLaunchUrl({
      payload: {
        assignment_id: query.assignmentId,
        target_id: targetId || query.targetId,
        thread_id: session.id,
      },
    }));
  };

  const openSessionAttempt = (session, attempt) => {
    if (!session?.id || !attempt?.id || !query.assignmentId) return;
    navigate(buildIeltsSpeakingLaunchUrl({
      payload: {
        assignment_id: query.assignmentId,
        target_id: targetId || query.targetId,
        thread_id: session.id,
        attempt_id: attempt.id,
      },
    }));
  };

  const resultDetails = result ? {
    ...result,
    ...serializeIeltsSpeakingConfig({ ...config, question: result.question || config.question, part: result.part || config.part }),
    recording_url: localRecording?.url || result.recording_url || '',
  } : null;
  const resultActions = getIeltsResultActions({ hasResult: Boolean(result), hasThread: Boolean(threadId), hasRecording: Boolean(resultDetails?.recording_url) });

  if (contextLoading) return <main className="mx-auto max-w-4xl p-4 md:p-8"><p className="text-center text-gray-600">{t('Loading IELTS speaking practice...')}</p></main>;

  if (assigned && phase === 'record' && isAuthenticIeltsConfig(config)) {
    return (
      <IeltsAuthenticSimulation
        config={config}
        assignmentTitle={assignment?.title}
        assignmentId={query.assignmentId}
        targetId={targetId || query.targetId}
        attemptId={attemptId || query.attemptId}
        threadId={threadId || query.threadId}
        initialResult={result}
        onBack={() => navigate('/student/quizzes')}
        t={t}
      />
    );
  }

  return (
    <main className="mx-auto max-w-4xl p-4 pb-16 md:p-8">
      <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
        <div>
          <button type="button" onClick={() => navigate(assigned ? '/student/quizzes' : '/student/self-check')} className="inline-flex items-center gap-2 text-sm font-semibold text-indigo-700 hover:underline">
            <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
            {t(assigned ? 'Back to My Practices' : 'Back to Self Check')}
          </button>
          {assigned && <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Teacher assignment')}</p>}
        </div>
        {phase === 'record' && (
          <button type="button" onClick={startNewThread} className="inline-flex items-center gap-2 rounded border border-gray-300 bg-white px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50">
            <PlusIcon className="h-4 w-4" aria-hidden="true" />
            {t('New speaking practice')}
          </button>
        )}
      </header>

      {error && <div className="mb-5 border border-rose-200 bg-rose-50 px-4 py-3 text-rose-800" role="alert">{error}</div>}

      {phase === 'setup' && (
        <>
          <ConfigForm config={config} onChange={setConfig} disabled={contextLoading} t={t} />
          <div className="mt-4 flex justify-end">
            <button type="button" onClick={startAutonomousThread} disabled={contextLoading} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2.5 font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-50">
              {contextLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" /> : <PlayIcon className="h-5 w-5" aria-hidden="true" />}
              {t('Start IELTS speaking practice')}
            </button>
          </div>
        </>
      )}

      {phase === 'history' && (
        <section className="border border-gray-200 bg-white p-5 sm:p-6">
          <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('Teacher assignment')}</div>
          <h1 className="mt-1 text-2xl font-bold text-gray-950" data-i18n-skip>{assignment?.title || t('IELTS Speaking Practice')}</h1>
          <p className="mt-2 text-sm text-gray-600">{t('Choose a session to continue, or open an earlier attempt to review its recording, score, and feedback.')}</p>
        </section>
      )}

      {phase === 'record' && (
        <>
          <section className="border border-gray-200 bg-white p-5 sm:p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700">{t('IELTS Speaking Practice')}</div>
                <h1 className="mt-1 text-2xl font-bold text-gray-950" data-i18n-skip>{assignment?.title || t('Independent practice')}</h1>
                <div className="mt-3 flex flex-wrap gap-2 text-sm text-gray-600">
                  <span className="border border-gray-200 bg-gray-50 px-2 py-1">{config.part ? t('Part {part}', { part: config.part }) : t('Part not specified')}</span>
                  <span className="border border-gray-200 bg-gray-50 px-2 py-1">{config.strict_timer ? t('Strict timer') : t('Soft timer')}</span>
                </div>
              </div>
              <div className="border border-indigo-100 bg-indigo-50 px-3 py-2 text-right text-sm text-indigo-900">
                <div className="text-xs font-semibold uppercase tracking-wide">{t('Assessment scope')}</div>
                <div className="mt-1 font-semibold">{getIeltsAssessmentScope(config) === 'general' ? t('General speaking assessment') : t('Prompt-based assessment')}</div>
              </div>
            </div>
            <div className="mt-5 border-l-2 border-indigo-400 pl-4">
              <div className="text-sm font-semibold text-gray-900">{config.question ? t('Speaking question') : t('General speaking assessment')}</div>
              <p className="mt-2 whitespace-pre-wrap text-base leading-7 text-gray-800" data-i18n-skip>{config.question || t('Speak about a topic you choose. No question will be generated.')}</p>
            </div>
          </section>

          <div className="mt-4">
            <IeltsSpeakingRecordingStatus state={recordingState} timer={timer} assessmentProgress={assessmentProgress} t={t} />
          </div>
          <div className="mt-4 flex flex-wrap gap-3">
            {(recordingState === 'idle' || recordingState === 'retryable_failure') && (
              <button type="button" onClick={startRecording} disabled={contextLoading} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2.5 font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60">
                <MicrophoneIcon className="h-5 w-5" aria-hidden="true" />
                {t('Start recording')}
              </button>
            )}
            {recordingState === 'result' && (
              <button type="button" onClick={practiceAgain} disabled={contextLoading} className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2.5 font-semibold text-white hover:bg-indigo-700 disabled:cursor-wait disabled:opacity-60">
                {contextLoading ? <ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" /> : <PlayIcon className="h-5 w-5" aria-hidden="true" />}
                {t('Practice again')}
              </button>
            )}
            {recordingState === 'requesting_microphone' && <button type="button" disabled className="inline-flex items-center gap-2 rounded bg-indigo-600 px-4 py-2.5 font-semibold text-white opacity-60"><ArrowPathIcon className="h-5 w-5 animate-spin" aria-hidden="true" />{t('Requesting microphone access...')}</button>}
            {['recording', 'soft_timeout'].includes(recordingState) && <button type="button" onClick={() => stopRecording('manual')} className="inline-flex items-center gap-2 rounded bg-rose-600 px-4 py-2.5 font-semibold text-white hover:bg-rose-700"><StopIcon className="h-5 w-5" aria-hidden="true" />{t('Stop and submit')}</button>}
            {recordingState === 'soft_timeout' && <button type="button" onClick={() => { const nextTimer = { ...timerRef.current, allow_overtime: true, status: 'recording' }; timerRef.current = nextTimer; setTimer(nextTimer); setRecordingState('recording'); }} className="rounded border border-gray-300 px-4 py-2.5 font-semibold text-gray-800 hover:bg-gray-50">{t('Continue recording')}</button>}
            {recordingState === 'retryable_failure' && localRecording?.blob && <button type="button" onClick={retryRecording} className="inline-flex items-center gap-2 rounded border border-indigo-300 px-4 py-2.5 font-semibold text-indigo-700 hover:bg-indigo-50"><ArrowPathIcon className="h-5 w-5" aria-hidden="true" />{t('Retry scoring')}</button>}
          </div>
        </>
      )}

      {result && resultDetails && (
        <section className="mt-7 border-t border-gray-200 pt-6">
          <div className="mb-5 flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="text-xs font-semibold uppercase tracking-wide text-emerald-700">{t('Result ready')}</div>
              <h2 className="mt-1 text-2xl font-bold text-gray-950">{t('Your IELTS speaking result')}</h2>
            </div>
            <div className="flex flex-wrap gap-2">
              {resultActions.canStartNewThread && <button type="button" onClick={startNewThread} className="inline-flex items-center gap-2 rounded border border-gray-300 px-3 py-2 text-sm font-semibold text-gray-800 hover:bg-gray-50"><PlusIcon className="h-4 w-4" aria-hidden="true" />{t('New speaking practice')}</button>}
            </div>
          </div>
          <IeltsSpeakingResultAdapter attempt={{ ...result, practice_type: 'ielts_speaking' }} details={resultDetails} t={t} />
        </section>
      )}

      {phase !== 'history' && <History entries={history} currentId={result?.attempt_id || result?.id} onSelect={selectHistory} t={t} locale={locale} />}
      {assigned && <SessionHistory sessions={sessions} currentThreadId={threadId} onResume={resumeSession} onSelectAttempt={openSessionAttempt} t={t} locale={locale} />}
      {phase === 'record' && recordingState === 'retryable_failure' && !localRecording?.blob && <p className="mt-5 text-sm text-gray-600">{t('You can request the microphone again when ready.')}</p>}
    </main>
  );
}

export { ConfigForm, getResponseResult, normalizeIeltsConfig, buildIeltsSpeakingLaunchUrl };
